import * as React from 'react';

import { useIntl } from 'react-intl';

import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Input } from 'src/registry/ui/Input';
import SingleDatePicker from 'src/registry/ui/SingleDatePicker';
import { storage } from 'src/registry/utils/storage';

type DataTableDateDropdownProps = {
    afterDate?: Date;
    beforeDate?: Date;
    setValue: ({ afterDate, beforeDate }: { afterDate: Date; beforeDate: Date }) => void;
    setLabel?: (label: string) => void;
    enableTime?: boolean;
    inline?: boolean;
};
export default function DataTableDateDropdown({
                                                  setValue,
                                                  setLabel,
                                                  enableTime,
                                                  inline,
                                                  beforeDate,
                                                  afterDate,
                                              }: DataTableDateDropdownProps) {
    const { formatMessage } = useIntl();
    const locale = storage.getLanguage();

    const [dateTimeFilter, setDateTimeFilter] = React.useState({
        afterTime: '00:00',
        beforeTime: '23:59',
        afterDate: afterDate ? new Date(afterDate.getTime() - afterDate.getTimezoneOffset() * 60 * 1000) : new Date(),
        beforeDate: beforeDate
            ? new Date(beforeDate.getTime() - beforeDate.getTimezoneOffset() * 60 * 1000)
            : new Date(),
    });
    const formatDate = React.useCallback((date: Date) => {
        return date
            .toLocaleDateString(locale, {
                year: 'numeric',
                month: 'numeric',
                day: '2-digit',
            })
            .replace('г.', '');
    }, []);
    return (
        <div className="flex flex-row gap-2 px-2">
            <div className="flex w-full flex-col items-start justify-between text-end">
                {!inline && <span className="w-full text-left text-sm">{formatMessage(tableMessages.from)}</span>}
                <SingleDatePicker
                    captionLayout="dropdown"
                    buttonValue={formatDate(dateTimeFilter.afterDate)}
                    value={dateTimeFilter.afterDate}
                    onChange={(date) => {
                        const { beforeDate } = dateTimeFilter;
                        if ((date && date.getTime() > beforeDate.getTime()) || !date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        const newDateTimeFilter = {
                            ...dateTimeFilter,
                            afterDate: utcDate,
                        };
                        setDateTimeFilter(newDateTimeFilter);
                        setValue({ afterDate: utcDate, beforeDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(utcDate)} - ${formatDate(beforeDate)}`);
                    }}
                />
                {enableTime && (
                    <div className="w-full">
                        <Input
                            type="time"
                            className="justify-center p-2 text-sm"
                            defaultValue={dateTimeFilter.afterTime}
                            onChange={(e) => {
                                const { beforeDate, afterDate } = dateTimeFilter;

                                if (enableTime) {
                                    const [hours, minutes] = e.target.value.split(':').map(Number);
                                    afterDate.setHours(hours, minutes);
                                }

                                if (afterDate.getTime() > beforeDate.getTime()) return;

                                const newDateTimeFilter = {
                                    ...dateTimeFilter,
                                    afterDate: afterDate,
                                };
                                setDateTimeFilter(newDateTimeFilter);
                                setValue({ afterDate, beforeDate });
                                if (!setLabel) return;
                                setLabel(`${formatDate(afterDate)} - ${formatDate(beforeDate)}`);
                            }}
                        />
                    </div>
                )}
            </div>

            {inline ? (
                <span className="flex items-center px-2 text-sm">{formatMessage(tableMessages.to)}</span>
            ) : (
                <div className="flex flex-col items-center justify-center">
                    <span className="text-sm" contentEditable={true} />
                </div>
            )}

            <div className="flex w-full flex-col items-start text-start align-baseline">
                {!inline && <span className="px-0.5 text-left text-sm">{formatMessage(tableMessages.to)}</span>}
                <SingleDatePicker
                    captionLayout="dropdown"
                    buttonValue={formatDate(dateTimeFilter.beforeDate)}
                    value={dateTimeFilter.beforeDate}
                    onChange={(date) => {
                        const { afterDate } = dateTimeFilter;
                        if ((date && afterDate.getTime() > date.getTime()) || !date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        const newDateTimeFilter = {
                            ...dateTimeFilter,
                            beforeDate: utcDate,
                        };
                        setDateTimeFilter(newDateTimeFilter);
                        setValue({ afterDate, beforeDate: utcDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(afterDate)} - ${formatDate(utcDate)}`);
                    }}
                />
                {enableTime && (
                    <div className="w-full">
                        <Input
                            type="time"
                            className="justify-center p-2 text-sm"
                            defaultValue={dateTimeFilter.beforeTime}
                            onChange={(e) => {
                                const { beforeDate, afterDate } = dateTimeFilter;
                                if (enableTime) {
                                    const [hours, minutes] = e.target.value.split(':').map(Number);
                                    beforeDate.setHours(hours, minutes);
                                }
                                if (afterDate.getTime() > beforeDate.getTime()) return;

                                const newDateTimeFilter = {
                                    ...dateTimeFilter,
                                    afterDate: afterDate,
                                };
                                setDateTimeFilter(newDateTimeFilter);
                                setValue({ afterDate, beforeDate });
                                if (!setLabel) return;
                                setLabel(`${formatDate(afterDate)} - ${formatDate(beforeDate)}`);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
